import { type ComponentProps, type ReactNode, useEffect, useState } from 'react';
import { Button, Icon } from '@opswat/ui';
import { useGTM } from 'hooks/useGTM';
import { cn } from 'services/cn';

export type AlertBannerVariant =
    'informative' | 'success' | 'error' | 'warning' | 'error-critical' | 'informative-success';

export interface AlertBannerProps extends ComponentProps<'div'> {
    hasIcon?: boolean;
    isCentered?: boolean;
    isCloseable?: boolean;
    icon?: ComponentProps<typeof Icon>['iconName'];
    variant?: AlertBannerVariant;
    text?: ReactNode;
    isVisible?: boolean;
    onClose?: ComponentProps<typeof Button>['onClick'];
}

export const AlertBanner = (props: AlertBannerProps) => {
    const {
        className,
        hasIcon = true,
        isCentered = false,
        isCloseable = false,
        icon,
        variant,
        onClose,
        text,
        isVisible = true,
        ...rest
    } = props;

    const [isNotificationVisible, setIsNotificationVisible] = useState(isVisible);
    const { trackClick } = useGTM();

    useEffect(() => {
        setIsNotificationVisible(isVisible);
    }, [isVisible]);

    const handleClose: AlertBannerProps['onClose'] = (event) => {
        trackClick('alert-banner-close');
        setIsNotificationVisible(false);
        onClose?.(event);
    };

    const getVariantInfo = () => {
        switch (variant) {
            case 'success':
                return {
                    bgClass: 'tw:bg-green-300 tw:dark:bg-green-1100',
                    icon: <Icon iconName='check' />,
                };
            case 'error':
                return {
                    bgClass: 'tw:bg-red-200 tw:dark:bg-red-1100',
                    icon: <Icon iconName='warning' />,
                };
            case 'error-critical':
                return {
                    bgClass: 'tw:bg-red-900 tw:dark:bg-red-1000',
                    textClass: 'tw:text-white',
                    icon: <Icon iconName='warning' className='tw:bg-white' />,
                };
            case 'warning':
                return {
                    bgClass: 'tw:bg-orange-300 tw:dark:bg-orange-1100',
                    icon: <Icon iconName='warning' />,
                };
            case 'informative-success':
                return {
                    bgClass: 'tw:bg-blue-900 tw:dark:bg-blue-1000',
                    textClass: 'tw:text-white',
                    icon: <Icon iconName='check' className='tw:bg-white' />,
                };
            case 'informative':
            default:
                return {
                    bgClass: 'tw:bg-neutrals-200 tw:dark:bg-neutrals-1100',
                    icon: <Icon iconName='message' />,
                };
        }
    };

    const { bgClass, textClass, icon: defaultIcon } = getVariantInfo();
    const isWhiteCloseButton = variant === 'informative-success' || variant === 'error-critical';

    if (!isNotificationVisible) {
        return null;
    }

    return (
        <div
            className={cn('tw:flex tw:max-h-116 tw:p-16 tw:items-center tw:relative tw:rounded-sm', bgClass, className)}
            {...rest}
        >
            <div
                className={cn(
                    'tw:flex tw:items-center tw:gap-8',
                    isCentered ? 'tw:justify-center tw:text-center tw:mx-auto tw:w-full' : 'tw:grow',
                )}
            >
                {hasIcon && <div className='tw:icon-container'>{icon ? <Icon iconName={icon} /> : defaultIcon}</div>}

                <div className={cn('tw:text-sm', textClass ?? 'tw:text-neutrals-1200 tw:dark:text-white')}>{text}</div>
            </div>

            {isCloseable && (
                <Button
                    onClick={handleClose}
                    aria-label='Close'
                    icon='close'
                    className={cn(
                        'tw:focus-visible:outline-1 tw:focus-visible:outline-offset-1 tw:focus-visible:outline-blue-800',
                        isCentered ? 'tw:absolute tw:right-16' : 'tw:ml-8',
                        isWhiteCloseButton && 'tw:**:data-icon:bg-white',
                    )}
                />
            )}
        </div>
    );
};
